/**
 * Production composition blocks (Phase 11.5.7 — literal realDemo clone). InfoBlock mirrors
 * the reference `common/info/landing-info` DOM exactly:
 *
 *   .sbp-info > .sbp-info__line (decorative bar)
 *            + .sbp-info__content > h1|h2 + p (subheading) [+ p (subtitle)]
 *            + Actions
 *
 * The reference info has NO eyebrow label — `eyebrow`/`eyebrowBar` only toggle the decorative
 * line (kept for a stable API; the label text is not rendered, per the reference). SectionHeader
 * = a centered InfoBlock used by list-style sections (services / reviews / faq / gallery).
 */
import type { ReactNode } from 'react'
import type { Action } from '../../schema'
import type { Align } from './types'
import { ALIGN_CLASS } from './types'
import { Eyebrow, Heading } from './typography'
import { Actions } from './actions'
import { cx } from './utils'

interface InfoBlockProps {
  readonly eyebrow?: string
  /** Show the decorative info line (reference always shows it above the heading). */
  readonly eyebrowBar?: boolean
  readonly heading?: string
  readonly subheading?: string
  /** Optional second paragraph (reference banner "subtitle" slot). */
  readonly subtitle?: string
  readonly headingLevel?: 1 | 2 | 3 | 4
  readonly headingId?: string
  readonly align?: Align
  readonly actions?: readonly Action[]
  readonly className?: string
  readonly children?: ReactNode
}

export function InfoBlock({
  heading,
  subheading,
  subtitle,
  headingLevel = 2,
  headingId,
  align = 'start',
  actions,
  className,
  children,
}: InfoBlockProps) {
  return (
    <div className={cx('sbp-info', ALIGN_CLASS[align], className)}>
      <Eyebrow />
      <div className="sbp-info__content">
        {heading ? (
          <Heading level={headingLevel} id={headingId}>
            {heading}
          </Heading>
        ) : null}
        {subheading ? <p>{subheading}</p> : null}
        {subtitle ? <p className="sbp-info__subtitle">{subtitle}</p> : null}
      </div>
      {children}
      <Actions actions={actions} align={align} />
    </div>
  )
}

type SectionHeaderProps = Omit<InfoBlockProps, 'align' | 'children'>

/** A centered InfoBlock used as a section header. */
export function SectionHeader(props: SectionHeaderProps) {
  return (
    <div className="sbp-section-header">
      <InfoBlock align="center" {...props} />
    </div>
  )
}
